import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Full-screen car video as the site background. Plays once and holds on the
 * final lights-on frame (no loop).
 * - "/"      → vivid hero treatment
 * - others   → dimmed + blurred ambient treatment (slowed playback)
 * - "/game"  → not rendered (Phaser owns the screen)
 * - prefers-reduced-motion → static poster image instead of video
 */
export function VideoBackground() {
  const { pathname } = useLocation();
  const isHero = pathname === "/";
  const isGame = pathname.startsWith("/game");

  const videoRef = useRef<HTMLVideoElement>(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Slow the loop down for the ambient (interior) background.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = isHero ? 1 : 0.6;
    }
  }, [isHero, reduced]);

  if (isGame) return null;

  return (
    <div
      className={`ro-bg-layer ${isHero ? "ro-bg-hero" : "ro-bg-ambient"}`}
      aria-hidden="true"
    >
      {reduced ? (
        <img
          className="ro-bg-media"
          src="/assets/video/ro-bg-poster.jpg"
          alt=""
        />
      ) : (
        <video
          ref={videoRef}
          className="ro-bg-media"
          autoPlay
          muted
          playsInline
          preload="auto"
          poster="/assets/video/ro-bg-poster.jpg"
        >
          <source src="/assets/video/ro-bg.webm" type="video/webm" />
          <source src="/assets/video/ro-bg.mp4" type="video/mp4" />
        </video>
      )}
      <div className="ro-bg-scrim" />
    </div>
  );
}

export default VideoBackground;
